import { getCookie } from "@insite/client-framework/Common/Cookies";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import NavigationDrawer from "@insite/content-library/Components/NavigationDrawer";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PopoverPresentationProps, PositionStyle } from "@insite/mobius/Popover";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// eslint-disable-next-line spire/fenced-imports
import { TS } from "@insite/shell/TS";
import { MenuPresentationProps } from "@ucs/MobiusOverrides/Menu/Menu";
import MainNavigationItem from "@ucs/Overrides/Components/MainNavigationItem";
import UCSColors from "@ucs/Utils/UCSColors";
import * as cssLinter from "css";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    showQuickOrder = "showQuickOrder",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showQuickOrder]: boolean;
        [fields.customCSS]: string;
    };
    isCompactHeaderFlyoutMenu: boolean;
    flyoutCustomCSS?: string;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    isCompactHeaderMainNavigation: boolean;
    сompactHeaderMainNavigationCustomCSS?: string;
}

interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        linkType: "Link" | "MegaMenu" | "CascadingMenu" | "CustomLinkList" | string;
        overrideTitle: string;
        title: string;
        numberOfColumns: number;
        maxDepth: number;
        blurbTitle: string;
        blurbDescription: string;
        showBrands: boolean;
        childLinks?: LinkModel[];
        titleLink?: LinkFieldValue;
        parentTitle?: string;
    };
}

export interface MappedLink {
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    isProductMenu?: boolean;
    blurbTitle?: string;
    blurbDescription?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { links = [] },
    } = ownProps;
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};
    const categoryProductsToLoad: string[] = [];

    for (const link of links) {
        let mappedLink = mapLink(link, state, categoryIdsToLoad);

        const {
            fields: {
                linkType,
                destination,
                openInNewWindow,
                overrideTitle,
                numberOfColumns,
                maxDepth,
                title,
                blurbTitle,
                blurbDescription,
                showBrands,
            },
        } = link;
        const { type, value } = destination;

        const depthToLoad = linkType === "MegaMenu" || linkType === "ProductMenu" ? 3 : maxDepth;

        /* let mappedLink: MappedLink | undefined; */

        if (type === "Url") {
            mappedLink = {
                url: value,
                title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overrideTitle) {
                    mappedLink.title = overrideTitle;
                }
            }
        } else if (type === "Category") {
            mappedLink = setupCategoryLink(
                state,
                value,
                categoryIdsToLoad,
                depthToLoad,
                overrideTitle,
                blurbTitle,
                blurbDescription,
                categoryProductsToLoad,
                state.data.categories.categoryIdsCalled,
            );
        }

        if (!mappedLink) {
            continue;
        }

        if (linkType === "ProductMenu" && showBrands && state.data.brands.byId) {
            // showBrands, state.data.brands.byId
            const brandsLink: MappedLink = {
                title: "By Brand",
                url: "/brands",
                children: [],
            };

            for (const i in state.data.brands.byId) {
                const brand = state.data.brands.byId[i];

                const brandLink: MappedLink = {
                    title: brand.name,
                    url: brand.detailPagePath,
                    children: [],
                };
                brandsLink?.children?.push(brandLink);
            }

            if (brandsLink && brandsLink.children && brandsLink.children.length > 5) {
                brandsLink.children.splice(5, brandsLink.children.length - 5);
                brandsLink.children.push({
                    title: "» see all",
                    url: "/brands",
                    children: [],
                });
            }

            mappedLink?.children?.push(brandsLink);
        }

        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.numberOfColumns = numberOfColumns;
        mappedLink.maxDepth = maxDepth;

        if (linkType === "MegaMenu" || linkType === "CascadingMenu") {
            mappedLink.childrenType = linkType;
        }

        if (linkType === "ProductMenu") {
            mappedLink.childrenType = "MegaMenu";
            mappedLink.isProductMenu = true;
        }

        mappedLinks.push(mappedLink);
    }

    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const { orderSettings } = getSettingsCollection(state);
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;

    return {
        links: mappedLinks,
        brands: state.data.brands.byId,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        categoryIdsToLoad,
        categoryProductsToLoad,
        allowQuickOrder: orderSettings.allowQuickOrder,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        displayChangeCustomerLink: session?.displayChangeCustomerLink,
        displayVmiNavigation:
            currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        displayModeSwitch: isAuthenticated && orderSettings.vmiEnabled && (isVmiAdmin || isVmiUser),
        currentMode,
        vmiPageLinks: isVmiAdmin
            ? [
                  getPageLinkByPageType(state, "VmiDashboardPage"),
                  getPageLinkByPageType(state, "VmiLocationsPage"),
                  getPageLinkByPageType(state, "VmiUsersPage"),
                  getPageLinkByPageType(state, "VmiBinsPage"),
                  getPageLinkByPageType(state, "VmiReportingPage"),
                  getPageLinkByPageType(state, "VmiOrderHistoryPage"),
              ]
            : isVmiUser
              ? [
                    getPageLinkByPageType(state, "VmiDashboardPage"),
                    getPageLinkByPageType(state, "VmiLocationsPage"),
                    getPageLinkByPageType(state, "VmiBinsPage"),
                    getPageLinkByPageType(state, "VmiReportingPage"),
                    getPageLinkByPageType(state, "VmiOrderHistoryPage"),
                ]
              : [],
    };
};

const mapLink = (
    link: LinkModel,
    state: ApplicationState,
    categoryIdsToLoad: Dictionary<number>,
    isChildLink = false,
) => {
    const {
        fields: {
            linkType,
            destination,
            openInNewWindow,
            overrideTitle: overrideTitleValue,
            numberOfColumns,
            maxDepth,
            title: titleValue,
            childLinks,
            parentTitle,
            titleLink,
        },
    } = link;
    const { type: typeValue, value: destinationValue } = destination;

    let type = typeValue;
    let value = destinationValue;
    let title = titleValue;
    let overrideTitle = overrideTitleValue;
    if (linkType === "CustomLinkList") {
        type = titleLink?.type ?? "Page";
        value = titleLink?.value ?? "";
        title = parentTitle ?? "";
        overrideTitle = parentTitle ?? "";
    }

    const depthToLoad = linkType === "MegaMenu" ? 3 : linkType === "CustomLinkList" || isChildLink ? 0 : maxDepth;

    let mappedLink: MappedLink | undefined;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: isChildLink ? overrideTitle : title,
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
            if (overrideTitle) {
                mappedLink.title = overrideTitle;
            }
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(
            state,
            value,
            categoryIdsToLoad,
            depthToLoad,
            overrideTitle,
            link.fields.blurbTitle ?? "", // blurbTitle
            link.fields.blurbDescription ?? "", // blurbDescription
            [], // categoryProductsToLoad
            state.data.categories.categoryIdsCalled,
        );
    }

    if (linkType === "CustomLinkList") {
        if (!mappedLink) {
            mappedLink = {
                title: parentTitle ?? title,
                url: "",
                excludeFromNavigation: false,
                childrenType: "CustomLinkList",
            };
        }
        let mappedChildren: MappedLink[] = [];
        if (childLinks && childLinks.length > 0) {
            for (const child of childLinks) {
                const childLink = mapLink(child, state, categoryIdsToLoad, true);
                if (childLink) {
                    mappedChildren.push(childLink);
                }
            }
        }
        mappedLink.children = mappedChildren;
    }

    if (!mappedLink) {
        return;
    }

    mappedLink.openInNewWindow = openInNewWindow;
    mappedLink.numberOfColumns = numberOfColumns;
    mappedLink.maxDepth = maxDepth;
    mappedLink.excludeFromNavigation =
        linkType === "CustomLinkList" || isChildLink ? false : mappedLink.excludeFromNavigation;

    if (linkType === "MegaMenu" || linkType === "CascadingMenu" || linkType === "CustomLinkList") {
        mappedLink.childrenType = linkType;
    }
    return mappedLink;
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    blurbTitle: string,
    blurbDescription: string,
    categoryProductsToLoad: string[],
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
                blurbTitle,
                blurbDescription,
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state, categoryProductsToLoad);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
    categoryProductsToLoad: string[],
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }

        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.properties?.main_nav_title || subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        if (subCategory.properties.main_nav_products_only && !categoryProductsToLoad.includes(subCategory.id)) {
            const productsDataView = getProductsDataView(state, { categoryId: subCategory.id });
            if (!productsDataView.value) {
                categoryProductsToLoad.push(subCategory.id);
            } else {
                productsDataView.value.forEach(product => {
                    const productLink: MappedLink = {
                        url: product.canonicalUrl,
                        title: product.productTitle,
                        children: [],
                    };

                    childLink?.children?.push(productLink);
                });
            }
        } else if (!subCategory.properties.main_nav_products_only) {
            loadSubCategories(
                childLink,
                subCategory.subCategoryIds,
                currentDepth + 1,
                maxDepth,
                state,
                categoryProductsToLoad,
            );
        }

        if (currentDepth > 1 && childLink && childLink.children && childLink.children.length > 5) {
            childLink.children.splice(5, childLink.children.length - 5);
            childLink.children.push({
                title: "» see all",
                url: childLink.url,
                children: [],
            });
        } else if (currentDepth === 1 && childLink && childLink.children && childLink.children.length > 10) {
            childLink.children.splice(10, childLink.children.length - 10);
            childLink.children.push({
                title: "» see all",
                url: childLink.url,
                children: [],
            });
        }
    }
};

const mapDispatchToProps = {
    loadCategories,
    loadBrands,
    loadProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface State {
    selectedLinkIndex?: number;
    mobileSearchModalIsOpen: boolean;
    brandsLoaded: boolean;
}

export interface MainNavigationStyles {
    menuHeight: number;
    container?: InjectableCss;
    itemWrapper?: InjectableCss;
    quickOrderItemWrapper?: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    megaMenu?: PopoverPresentationProps;
    megaMenuGridContainer?: GridContainerProps;
    megaMenuGridItem?: GridItemProps;
    megaMenuHeading?: LinkPresentationProps;
    megaMenuLink?: LinkPresentationProps;
    megaMenuLinkHeader?: LinkPresentationProps;
    mobileWrapper?: InjectableCss;
    compactFlyoutMenu?: InjectableCss;
    mobileMenuWrapper?: InjectableCss;
    mobileSearchButton?: ButtonPresentationProps;
    mobileSearchWrapper?: InjectableCss;
    mobileSearchModal?: ModalPresentationProps;
    mobileSearchInputWrapper?: InjectableCss;
    mobileSearchInputStyles?: SearchInputStyles;
    mobileSearchModalCloseIcon?: IconProps;
}

export const mainNavigationStyles: MainNavigationStyles = {
    menuHeight: 50,
    container: {
        css: css`
            color: ${getColor("common.background")};
            padding: 0 0 0 20px;
            display: flex;
            justify-content: flex-end;
        `,
    },
    megaMenuGridContainer: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    megaMenuGridItem: {
        css: css`
            min-width: 150px;
            margin: 0px 20px;
        `,
    },
    itemWrapper: {
        css: css`
            position: relative;
            overflow: visible;
            display: flex;
            &:hover span + div {
                display: block;
            }
            &:first-child {
                margin-left: -10px;
            }
        `,
    },
    quickOrderItemWrapper: {
        css: css`
            margin-left: auto;
            margin-right: 0;
        `,
    },
    megaMenuHeading: {
        css: css`
            span {
                font-weight: bold;
                font-size: 18px;
                font-weight: 500;
            }
            padding-bottom: 8px;
            border-bottom: 1px solid ${UCSColors.gray07};
            margin-bottom: 16px;
            width: 100%;
        `,
    },
    megaMenuLink: {
        css: css`
            font-weight: 300;
            font-size: 16px;
            margin-bottom: 8px;
        `,
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            height: 50px;
            margin: 0 10px;
            height: 77px;
            padding: 3px 0px;

            &:hover {
                padding-bottom: 0px;
                border-bottom: 3px solid ${getColor("primary")};
            }
        `,
    },
    menuItemIcon: {
        css: css`
            margin: -3px 0 0 10px;
        `,
        size: 18,
        color: "secondary.contrast",
    },
    menuItemTypography: {
        // variant: "headerSecondary",
        color: UCSColors.gray03,
        size: "18px",
        weight: 500,
    },
    megaMenu: {
        transitionDuration: "short",
        contentBodyProps: {
            _height: "500px",
            css: css`
                padding: 20px 30px;
                overflow-y: hidden;
            `,
        },
    },
    mobileSearchButton: {
        size: 48,
        buttonType: "solid",
        color: "secondary",
        css: css`
            padding: 0 10px;
        `,
    },
    mobileWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            button {
                padding: 0px;
            }
        `,
    },
    compactFlyoutMenu: {
        css: css`
            & button {
                background: transparent;
                border: none;
                &:hover {
                    background: rgba(0, 0, 0, 0.3);
                }

                svg {
                    color: ${props => resolveColor(props.theme.header.linkColor, props.theme)};
                }
            }
        `,
    },
    mobileSearchWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileMenuWrapper: {
        css: css`
            text-align: right;
        `,
    },
    mobileSearchInputWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    mobileSearchInputStyles: {
        input: {
            cssOverrides: {
                formField: css`
                    width: 100%;
                `,
            },
        },
        popover: {
            wrapperProps: {
                css: css`
                    width: calc(100% - 45px);
                `,
            },
        },
        popoverContentBody: {
            as: "div",
            _height: "100%",
            css: css`
                box-shadow: none;
            `,
        },
        autocompleteWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
        autocompleteColumnWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                padding: 0 50px 0 30px;
            `,
        },
        searchHistoryStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    padding: 0 50px 15px 30px;
                `,
            },
        },
        autocompleteProductsStyles: {
            titleLink: {
                css: css`
                    width: 100%;
                `,
            },
            erpNumberText: {
                css: css`
                    width: 100%;
                    text-align: left;
                    margin: 5px 0 0 0;
                `,
            },
        },
    },
    mobileSearchModalCloseIcon: {
        src: X,
        size: 18,
        css: css`
            margin-left: 10px;
        `,
    },
    cascadingMenu: {
        delay: 400,
        cssOverrides: {
            menu: css`
                background-color: ${getColor("common.background")};
                padding: 0px;
                width: 180px;
                ul {
                    padding: 0px;
                }
                li {
                    border: none !important;
                    margin-bottom: 8px;
                    padding-bottom: 0px;
                    padding-top: 0px;
                    &:first-child {
                        margin-top: 16px;
                    }
                    &:last-child {
                        margin-bottom: 0;
                        a {
                            &:last-child {
                                border-bottom: none;
                            }
                        }
                    }
                    a {
                        min-height: 0;
                        display: inline-flex;
                        padding-bottom: 8px;
                        border-bottom: 1px solid ${UCSColors.gray07};
                        width: 100%;
                        &:hover {
                            background: transparent;
                            font-weight: inherit;
                        }
                    }
                    span {
                        font-size: 18px !important;
                        color: ${UCSColors.gray03} !important;
                        font-weight: 500 !important;
                        padding-bottom: 0px;
                        padding-top: 0px;
                    }
                }
            `,
        },
    },
};

const defaultCustomCss = `.mobile-wrapper {
}

.mobile-menu-wrapper {
}

.menu-trigger-button {
}

.menu-trigger-button-icon {
}

.drawer {
}

.drawer-section-wrapper {
}

.panel-menu {
}

.main-navigation-row {
}

.panel-section-wrapper {
}

.main-navigation-row-icon {
}

.user-row-text {
}

.punch-out-navigation-row-icon {
}

.punch-out-user-row-text {
}

.main-navigation-row-text {
}

.logo-links-row {
}

.logo-link-title {
}

.language-image {
}

.menu-row-icon {
}

.change-customer-row {
}

.change-customer-row-container {
}

.fulfillment-method-grid-item {
}

.addresses-grid-item {
}

.pick-up-address-grid-item {
}

.apply-button-grid-item {
}

.sign-out-row {
}

.sign-out-row-text {
}

.mobile-search-wrapper {
}

.mobile-search-button {
}

.mobile-search-button-icon {
}

.container {
}

.item-wrapper {
}

.cascading-menu {
}

.menu-item {
}

.mega-menu {
}

.mega-menu-grid-container {
}

.mega-menu-grid-item {
}

.mega-menu-heading-link {
}

.mega-menu-link {
}

.quick-order-item-wrapper {
}

.quick-order-link {
}

.vmi-page-link {
}

.mobile-search-modal {
}`;

const styles = mainNavigationStyles;

class MainNavigation extends React.Component<Props, State> {
    container = React.createRef<HTMLDivElement | null>();
    mobileSearchInput = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            mobileSearchModalIsOpen: false,
            brandsLoaded: false,
        };
    }

    componentDidMount = () => {
        if (!this.state.brandsLoaded) {
            this.props.loadBrands(); // Always load all brands...
            this.setState({ brandsLoaded: true });
        }
    };

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
        this.loadProductsIfNeeded();
        setMainNavigation({
            close: () => {
                this.setState({
                    selectedLinkIndex: undefined,
                });
            },
            openMenu: (index: number) => {
                this.setState({
                    selectedLinkIndex: index,
                });
            },
        });
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        for (const categoryId of Object.keys(categoryIdsToLoad)) {
            if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                const maxDepth = categoryIdsToLoad[categoryId];
                if (!this.props.categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                    this.props.loadCategories(getParameter(categoryId, maxDepth));
                }
            }
        }
    }

    loadProductsIfNeeded() {
        const { categoryProductsToLoad } = this.props;
        if (categoryProductsToLoad) {
            categoryProductsToLoad.forEach((id: string) => {
                this.props.loadProducts({
                    categoryId: id,
                });
            });
        }
    }

    getMobileSearchAutocompletePosition = (element: React.RefObject<HTMLUListElement | null>) => {
        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top = rect.top + rect.height;
            positionStyle.left = 0;
            positionStyle.width = `${document.documentElement.clientWidth}px`;
            positionStyle.height = `${document.documentElement.clientHeight - positionStyle.top}px`;
        }

        return positionStyle;
    };

    mobileSearchButtonClickHandler = () => {
        this.setState({ mobileSearchModalIsOpen: true }, () => {
            setTimeout(() => {
                if (this.mobileSearchInput?.current) {
                    this.mobileSearchInput.current.focus();
                    this.mobileSearchInput.current.click();
                }
            }, 250);
        });
    };

    mobileSearchModalCloseHandler = () => {
        this.setState({ mobileSearchModalIsOpen: false });
    };

    render() {
        const {
            links,
            quickOrderLink,
            fields: { showQuickOrder, customCSS },
            id,
            allowQuickOrder,
            isCompactHeaderFlyoutMenu,
            flyoutCustomCSS,
            showFlyoutPricingToggle,
            showFlyoutInventoryToggle,
            isCompactHeaderMainNavigation,
            сompactHeaderMainNavigationCustomCSS,
            vmiPageLinks,
            displayVmiNavigation,
            displayModeSwitch,
            currentMode,
        } = this.props;
        const { selectedLinkIndex } = this.state;

        const showQuickOrderLink = showQuickOrder && allowQuickOrder;

        if (isCompactHeaderFlyoutMenu) {
            const flyoutCustomCssWrapper = {
                css: css`
                    ${flyoutCustomCSS}
                `,
            };
            return (
                <StyledWrapper {...flyoutCustomCssWrapper}>
                    <StyledWrapper className="flyout-menu-wrapper" {...styles.compactFlyoutMenu}>
                        <NavigationDrawer
                            links={links}
                            showQuickOrder={showQuickOrderLink}
                            quickOrderLink={quickOrderLink}
                            displayVmiNavigation={displayVmiNavigation}
                            vmiPageLinks={vmiPageLinks}
                            displayModeSwitch={displayModeSwitch}
                            currentMode={currentMode}
                            isCompactHeaderFlyout={true}
                            showFlyoutPricingToggle={showFlyoutPricingToggle}
                            showFlyoutInventoryToggle={showFlyoutInventoryToggle}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            );
        }

        if (isCompactHeaderMainNavigation) {
            const mainNavigationCustomCSSWrapper = {
                css: css`
                    ${сompactHeaderMainNavigationCustomCSS}
                `,
            };
            return (
                <StyledWrapper {...mainNavigationCustomCSSWrapper}>
                    {displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                {vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <Link
                                                className="vmi-page-link"
                                                key={link.id}
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id={link.id}
                                                href={link.url}
                                            >
                                                {link.title}
                                            </Link>
                                        ),
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {!displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            {links.map((link, index) => {
                                return (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                        <MainNavigationItem
                                            index={index}
                                            link={link}
                                            styles={styles}
                                            container={this.container}
                                            isOpen={index === selectedLinkIndex}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                        />
                                    </StyledWrapper>
                                );
                            })}
                            {showQuickOrderLink && quickOrderLink && (
                                <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                    <StyledWrapper
                                        className="quick-order-item-wrapper"
                                        {...styles.quickOrderItemWrapper}
                                    >
                                        <Link
                                            className="quick-order-link"
                                            typographyProps={styles.menuItemTypography}
                                            color={styles.menuItemTypography?.color}
                                            {...styles.menuItem}
                                            id="quickOrder"
                                            href={quickOrderLink.url}
                                        >
                                            {quickOrderLink.title}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            );
        }

        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="mobile-wrapper" {...styles.mobileWrapper}>
                    <Hidden className="mobile-menu-wrapper" above="lg" {...styles.mobileMenuWrapper}>
                        <NavigationDrawer
                            links={links}
                            showQuickOrder={showQuickOrderLink}
                            quickOrderLink={quickOrderLink}
                            displayVmiNavigation={displayVmiNavigation}
                            vmiPageLinks={vmiPageLinks}
                            displayModeSwitch={displayModeSwitch}
                            currentMode={currentMode}
                        />
                    </Hidden>
                </StyledWrapper>
                <Hidden below="xl">
                    {!displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            {links.map((link, index) => {
                                return (
                                    <StyledWrapper className="item-wrapper" {...styles.itemWrapper} key={index}>
                                        <MainNavigationItem
                                            index={index}
                                            link={link}
                                            styles={styles}
                                            container={this.container}
                                            isOpen={index === selectedLinkIndex}
                                            displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                        />
                                    </StyledWrapper>
                                );
                            })}
                            {showQuickOrderLink && quickOrderLink && (
                                <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                    <StyledWrapper
                                        className="quick-order-item-wrapper"
                                        {...styles.quickOrderItemWrapper}
                                    >
                                        <Link
                                            className="quick-order-link"
                                            typographyProps={styles.menuItemTypography}
                                            color={styles.menuItemTypography?.color}
                                            {...styles.menuItem}
                                            id="quickOrder"
                                            href={quickOrderLink.url}
                                        >
                                            {quickOrderLink.title}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    {displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                {vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <Link
                                                className="vmi-page-link"
                                                key={link.id}
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id={link.id}
                                                href={link.url}
                                            >
                                                {link.title}
                                            </Link>
                                        ),
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </Hidden>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const mainNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MainNavigation),
    definition: {
        group: "Header",
        icon: "magnifying-glass",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                onEditRow: (index: number, dispatch) => {
                    dispatch({
                        type: "SendToSite/OpenMainNavigation",
                        index,
                    });
                },
                onDoneEditingRow: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                onLoad: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                getDisplay: (item: HasFields, state) => {
                    const { type: typeValue, value: destinationValue } = item.fields.destination;
                    const { overrideTitle, title, linkType, parentTitle, titleLink } = item.fields;
                    let value = destinationValue;
                    let type = typeValue;
                    if (linkType === "CustomLinkList") {
                        if (parentTitle) {
                            return parentTitle;
                        }
                        type = titleLink.type;
                        value = titleLink.value;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Mega Menu",
                                value: "MegaMenu",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                            {
                                displayName: "Product Menu",
                                value: "ProductMenu",
                            },
                            {
                                displayName: "Custom Link List",
                                value: "CustomLinkList",
                            },
                        ],
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item =>
                            item.fields.destination.type !== "Url" && item.fields.linkType !== "CustomLinkList",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                        isVisible: item => item.fields.linkType !== "CustomLinkList",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type === "Url",
                    },
                    {
                        name: "parentTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.linkType === "CustomLinkList",
                        validate: (value, item) => {
                            if (!value && item.fields.titleLink.value === "") {
                                return TS("Either Title or Destination must have a value");
                            }

                            if (!value && item.fields.titleLink.type === "Url") {
                                return TS("URLs must have a corresponding title");
                            }

                            return null;
                        },
                    },
                    {
                        name: "titleLink",
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: item => item.fields.linkType === "CustomLinkList",
                    },
                    {
                        name: "childLinks",
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields, state) => {
                            const {
                                destination: { type, value },
                                overrideTitle,
                            } = item.fields;
                            if (overrideTitle) {
                                return overrideTitle;
                            }
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return link?.title;
                            }
                            if (type === "Category") {
                                let displayValue = "";
                                const { categories } = (state as any).pageEditor;
                                if (!categories) {
                                    displayValue = "Loading...";
                                } else {
                                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                    if (matchingCategories.length === 0) {
                                        displayValue = "Unknown";
                                    } else {
                                        displayValue = matchingCategories[0].shortDescription;
                                    }
                                }

                                return displayValue;
                            }
                            if (type === "Url") {
                                return value;
                            }
                            return value;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isRequired: true,
                            },
                            {
                                name: "overrideTitle",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                        isVisible: item => item.fields.linkType === "CustomLinkList",
                    },
                    {
                        name: "numberOfColumns",
                        editorTemplate: "DropDownField",
                        defaultValue: 6,
                        options: [{ value: 2 }, { value: 3 }, { value: 4 }, { value: 6 }],
                        isRequired: true,
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "MegaMenu" || item.fields.linkType === "ProductMenu",
                    },
                    {
                        name: "blurbTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "MegaMenu" || item.fields.linkType === "ProductMenu",
                    },
                    {
                        name: "blurbDescription",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        isVisible: (item: HasFields) =>
                            item.fields.linkType === "MegaMenu" || item.fields.linkType === "ProductMenu",
                    },
                    {
                        name: "showBrands",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isRequired: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "ProductMenu",
                    },
                    {
                        name: "maxDepth",
                        editorTemplate: "DropDownField",
                        defaultValue: 3,
                        options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "CascadingMenu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.showQuickOrder,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default mainNavigation;
